/**
 * HEROs API contract — the DTO surface every other workspace imports.
 *
 * Shapes are derived from PROJECT_BRIEF §3 and the prototype `data.js`. These
 * are the over-the-wire/in-memory client types; the Prisma schema (task 2) is
 * the persistence model and need not match field-for-field, but route handlers
 * MUST serialize to these shapes. Treat this file as STABLE — additive changes
 * only once feature waves start, so parallel agents don't conflict.
 *
 * Conventions:
 *  - All ids are strings.
 *  - All dates that are calendar dates use ISO-8601 'YYYY-MM-DD'; timestamps use
 *    full ISO-8601. Month index (0–11) is a DISPLAY aid only (brief §5).
 *  - Money is integer THB (no minor units in the prototype).
 */
/** Effective earning for a split = overrideAmount when set, else round(base × pct / 100).
 *  Shared so every UI surface computes individual earnings identically. */
export function effectiveEarning(base, split) {
    if (split.overrideAmount !== undefined && split.overrideAmount !== null) {
        return Math.max(0, Math.round(split.overrideAmount));
    }
    return Math.round((base * split.pct) / 100);
}
/* --- Compensation money model (senior request) — shared so web + server agree --- */
/** Central-fund skim amount = round(actualReceived × clamp(centralFundPct, 0..100) / 100). */
export function centralFundAmount(actualReceived, centralFundPct) {
    const pct = Math.min(100, Math.max(0, centralFundPct ?? 0));
    return Math.round((actualReceived ?? 0) * pct / 100);
}
/** THB left to allocate across tasks after the central-fund skim. */
export function compensationRemainder(actualReceived, centralFundPct) {
    return Math.max(0, Math.round(actualReceived ?? 0) - centralFundAmount(actualReceived, centralFundPct));
}
/** A %-driven task's base = round(remainder × max(0, allocPct) / 100). LEGACY/ungrouped path. */
export function allocBase(remainder, allocPct) {
    return Math.round(remainder * Math.max(0, allocPct ?? 0) / 100);
}
/* --- Compensation 3-level grouping (senior request) — shared so web + server agree --- */
/** A group's pot = round(remainder × clamp(groupPct, 0..100) / 100). */
export function groupPotAmount(remainder, groupPct) {
    const pct = Math.min(100, Math.max(0, groupPct ?? 0));
    return Math.round(remainder * pct / 100);
}
/** A grouped %-driven task's base = round(groupPot × max(0, groupTaskPct) / 100). */
export function groupedBase(remainder, groupPct, groupTaskPct) {
    return Math.round(groupPotAmount(remainder, groupPct) * Math.max(0, groupTaskPct ?? 0) / 100);
}
/** Integer largest-remainder (Hamilton) apportionment: split `total` THB across `weights`
 *  so the parts are non-negative integers summing EXACTLY to `total` (when total ≥ 0).
 *  Used to heal rounding so a group's per-task bases sum exactly to its pot. The leftover
 *  baht go to the largest fractional remainders (ties → earlier index). */
export function apportion(total, weights) {
    const n = weights.length;
    if (n === 0)
        return [];
    const t = Math.round(total);
    const sumW = weights.reduce((s, w) => s + Math.max(0, w), 0);
    if (sumW <= 0 || t <= 0)
        return weights.map(() => 0);
    const exact = weights.map((w) => (t * Math.max(0, w)) / sumW);
    const floor = exact.map((x) => Math.floor(x));
    let used = floor.reduce((s, x) => s + x, 0);
    let leftover = t - used;
    // Distribute leftover baht to the largest fractional parts.
    const order = exact
        .map((x, i) => ({ i, frac: x - Math.floor(x) }))
        .sort((a, b) => (b.frac - a.frac) || (a.i - b.i));
    const out = floor.slice();
    for (let k = 0; leftover > 0 && k < order.length; k++, leftover--) {
        out[order[k].i] += 1;
    }
    return out;
}
/** Canonical "Group of variable" choices for non-lab variables — shared so the
 *  Admin variable library and the Project-Builder variable picker offer the SAME
 *  dropdown (rendered as a datalist so a custom group can still be typed). */
export const VAR_GROUPS = [
    'Baseline characteristics',
    'Demographics',
    'Vital signs',
    'Anthropometrics',
    'Medical history',
    'Comorbidities',
    'Medication',
    'Laboratory',
    'Imaging',
    'Lifestyle / behaviour',
    'Socioeconomic',
    'Outcome / endpoint',
    'Other',
];
/** Resolve the cost of a DM item given a (possibly empty) per-project override. */
export function dmItemCost(item, sel) {
    if (item.isFixedMin)
        return Math.max(0, Math.round(item.fixedCost ?? 0));
    const units = sel?.units ?? item.estUnits;
    const price = sel?.unitPrice ?? item.unitPrice;
    return Math.max(0, Math.round(units * price));
}
/** The seeded defaults for the singleton analysis-cost formula. */
export const DEFAULT_ANALYSIS_FORMULA = {
    minutesPerSimple: 10,
    minutesPerComplex: 20,
    hourlyRate: 2000,
    complexMultiplier: 1.0,
};
//# sourceMappingURL=contract.js.map